/**
 * KIT Category detail — flat list of curated recommendations.
 *
 * MVP intentionally does NOT split products into primary vs.
 * alternative bands. All products supplied for a category render as
 * equal recommendations in JSON order. The optional `featured`
 * field is preserved in the schema for future supervisor-authored
 * highlighting but is not surfaced here.
 */
import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useRouteBase } from "../../context/ApiBaseContext";
import {
    getCatalogueForDepartment,
    getCategoryBySlug,
    getProductsForCategory,
} from "../../data/kitCatalogue";
import { resolveKitCategoryIcon } from "../../components/kit/iconMap";
import { KitProductRow } from "../../components/kit/KitProductRow";
import { KitAffiliateDisclosure } from "../../components/kit/KitAffiliateDisclosure";
import ProjectHeader from "../../components/ProjectHeader";

export default function KitCategoryPage({ department }) {
    const { projectId, categorySlug } = useParams();
    const routeBase = useRouteBase();

    const catalogue = useMemo(() => getCatalogueForDepartment(department), [department]);
    const category = useMemo(() => getCategoryBySlug(catalogue, categorySlug), [catalogue, categorySlug]);
    const products = useMemo(() => getProductsForCategory(catalogue, categorySlug), [catalogue, categorySlug]);

    const Icon = category ? resolveKitCategoryIcon(category.icon) : null;

    return (
        <div data-testid="kit-category-page" className="min-h-screen">
            {/* Iter-158.13.2.20f.29 — KIT category page adopts the shared
                `<ProjectHeader>`. Previous bespoke `<header>` (custom
                `kit-category-back` button + local title
                `category.name`) has been removed. Subtitle
                deliberately stays "Hair & Makeup" — the same as
                the KIT landing — because the category identity is
                already carried by the page content h1 below and does
                not need to be duplicated in the application header.
                See rule: "The content changes. The frame stays put."

                Iter-158.13.2.20f.30 — Subtitle trimmed from
                `Hair & Makeup · KIT` to `Hair & Makeup` for the same
                reason as the landing: the page content already reads
                "HAIR & MAKEUP KIT / {category.name}", so keeping
                "· KIT" in the frame just repeats the local
                identity. */}
            <ProjectHeader subtitle="Hair & Makeup" />

            <main className="max-w-2xl mx-auto px-5 pt-6 pb-24">
                {!catalogue || !category ? (
                    <div
                        data-testid="kit-category-missing"
                        className="rounded-xl border border-dashed border-white/10 p-6 text-center"
                    >
                        <h1 className="font-display text-xl font-bold text-white">
                            Category not found
                        </h1>
                        <p className="text-neutral-400 text-sm mt-2">
                            This KIT category isn&rsquo;t available.
                        </p>
                        <Link
                            to={`${routeBase}/${projectId}/kit`}
                            data-testid="kit-category-missing-back"
                            className="tap mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-full text-[12px] uppercase tracking-[0.2em] bg-primary text-primary-foreground"
                        >
                            Back to KIT
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="mb-6 flex items-start gap-3">
                            <span className="h-11 w-11 rounded-lg bg-cyan-400/10 text-cyan-300 flex items-center justify-center shrink-0">
                                {Icon ? <Icon size={20} /> : null}
                            </span>
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                                    {catalogue.display_name}
                                </div>
                                <h1
                                    data-testid="kit-category-heading"
                                    className="font-display text-3xl font-black text-white mt-1"
                                >
                                    {category.name}
                                </h1>
                                {category.description ? (
                                    <p className="text-neutral-400 text-sm mt-2 leading-relaxed max-w-prose">
                                        {category.description}
                                    </p>
                                ) : null}
                            </div>
                        </div>

                        {products.length === 0 ? (
                            <p
                                data-testid="kit-category-empty"
                                className="rounded-xl border border-dashed border-white/10 p-6 text-center text-sm text-neutral-500"
                            >
                                No recommendations in this category yet.
                            </p>
                        ) : (
                            <div
                                data-testid="kit-product-list"
                                className="space-y-2"
                            >
                                {products.map((p) => (
                                    <KitProductRow
                                        key={p.id}
                                        product={p}
                                        department={department}
                                        categorySlug={category.slug}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}

                <KitAffiliateDisclosure />
            </main>
        </div>
    );
}
